import React, { useState, useEffect } from 'react';
// Chart components temporarily disabled
// import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { 
  ChevronUpIcon, 
  ChevronDownIcon, 
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { paperTradingApi, exchangeApi } from '@/services/api';
import { Portfolio, Trade, PerformanceData } from '@/types';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import SummaryCard from '@/components/UI/SummaryCard';
import PerformanceChart from '@/components/Charts/PerformanceChart';
import AllocationChart from '@/components/Charts/AllocationChart';
import RecentTradesTable from '@/components/Tables/RecentTradesTable';
import MarketSentimentWidget from '@/components/UI/MarketSentimentWidget';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

interface DashboardData {
  portfolio: Portfolio | null;
  performance: PerformanceData | null;
  liveBalance: any | null; // Live exchange balance from Kraken/other exchanges
  recentTrades: Trade[];
  fearGreedData: any | null; // Fear & Greed index data
  newsSentimentData: any | null; // News sentiment data
  loading: boolean;
  error: string | null;
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData>({
    portfolio: null,
    performance: null,
    liveBalance: null,
    recentTrades: [],
    fearGreedData: null,
    newsSentimentData: null,
    loading: true,
    error: null,
  });

  const [_refreshInterval, _setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [_lastUpdated, _setLastUpdated] = useState<Date>(new Date());

  // Real data fetching function
  const fetchDashboardData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      // PERFORMANCE OPTIMIZATION: Fetch data from ALL endpoints in parallel (including widgets)
      // Calculate date range for 1 year
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();
      
      // Import API client for market data
      const { apiClient } = await import('@/services/api');
      
      console.log('[DASHBOARD] Starting parallel API loading at:', new Date().toISOString());
      const startTime = Date.now();
      
      // TRUE PARALLEL LOADING: All 6 APIs execute simultaneously
      const [portfolioRes, performanceRes, tradesRes, liveBalanceRes, fearGreedRes, newsSentimentRes] = await Promise.allSettled([
        paperTradingApi.getPortfolio(),
        paperTradingApi.getPerformance({ dateFrom: startDate, dateTo: endDate }),
        paperTradingApi.getTrades({ limit: 10 }),
        exchangeApi.getAggregatedBalance({ includeZero: true }), // Optimized aggregated endpoint
        apiClient.get('/market/fear-greed', { limit: 2 }), // Fear & Greed data
        apiClient.get('/market/news-sentiment', { hours: 24 }) // News sentiment data
      ]);
      
      const loadTime = Date.now() - startTime;
      console.log(`[DASHBOARD] Parallel API loading completed in ${loadTime}ms`);

      // Process portfolio data
      let portfolio = null;
      if (portfolioRes.status === 'fulfilled') {
        portfolio = (portfolioRes.value as any).portfolio;
        console.log('[DASHBOARD] Portfolio data loaded successfully');
      } else {
        console.warn('[DASHBOARD] Portfolio fetch failed:', portfolioRes.reason);
      }

      // Process performance data
      let performanceData = null;
      if (performanceRes.status === 'fulfilled') {
        performanceData = (performanceRes.value as any).performance;
        console.log('[DASHBOARD] Performance data loaded successfully');
      } else {
        console.warn('[DASHBOARD] Performance fetch failed:', performanceRes.reason);
      }

      // Process recent trades
      let recentTrades = [];
      if (tradesRes.status === 'fulfilled') {
        recentTrades = tradesRes.value.data || [];
        console.log(`[DASHBOARD] Loaded ${recentTrades.length} recent trades`);
      } else {
        console.warn('[DASHBOARD] Trades fetch failed:', tradesRes.reason);
      }

      // Process live exchange balance (CRITICAL: Using optimized aggregated endpoint)
      let liveBalance = null;
      if (liveBalanceRes.status === 'fulfilled') {
        liveBalance = (liveBalanceRes.value as any).data;
        console.log('[DASHBOARD] Live balance loaded successfully');
      } else {
        console.warn('[DASHBOARD] Live balance fetch failed:', liveBalanceRes.reason);
      }

      // Process Fear & Greed data
      let fearGreedData = null;
      if (fearGreedRes.status === 'fulfilled') {
        // Handle API response structure - check both direct and wrapped data access
        const response = fearGreedRes.value as any;
        fearGreedData = response.fearGreedIndex || response.data?.fearGreedIndex;
        console.log('[DASHBOARD] Fear & Greed data loaded successfully');
      } else {
        console.warn('[DASHBOARD] Fear & Greed fetch failed:', fearGreedRes.reason);
      }

      // Process News Sentiment data
      let newsSentimentData = null;
      if (newsSentimentRes.status === 'fulfilled') {
        newsSentimentData = (newsSentimentRes.value as any).data;
        console.log('[DASHBOARD] News sentiment data loaded successfully');
      } else {
        console.warn('[DASHBOARD] News Sentiment fetch failed:', newsSentimentRes.reason);
      }

      // Enhanced error handling: Only show error if ALL core requests failed
      if (portfolioRes.status === 'rejected' && 
          performanceRes.status === 'rejected' && 
          tradesRes.status === 'rejected' &&
          liveBalanceRes.status === 'rejected') {
        throw new Error('Failed to load dashboard data');
      }

      setData({
        portfolio,
        performance: performanceData,
        recentTrades,
        liveBalance,
        fearGreedData,
        newsSentimentData,
        loading: false,
        error: null,
      });

      _setLastUpdated(new Date());
      console.log(`[DASHBOARD] Dashboard fully loaded in ${loadTime}ms with parallel API execution`);

    } catch (error) {
      console.error('[DASHBOARD] Dashboard data fetch error:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load dashboard data'
      }));
    }
  };

  useEffect(() => {
    // Load real data on component mount
    fetchDashboardData();
    
    // Set up auto-refresh every 5 minutes
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 5 * 60 * 1000);
    
    _setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const handleRefresh = () => {
    fetchDashboardData();
  };

  if (data.loading && !data.portfolio) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-bearish mx-auto mb-4" />
          <h3 className="text-heading-3 text-primary-custom mb-2">Error Loading Dashboard</h3>
          <p className="text-body text-secondary-custom mb-6">{data.error}</p>
          <button
            onClick={handleRefresh}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { portfolio, performance, liveBalance, fearGreedData, newsSentimentData } = data; // recentTrades unused for now

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-display font-bold text-primary-custom">Trading Dashboard</h1>
          <p className="text-body text-secondary-custom">Monitor your portfolio performance and market opportunities</p>
        </div>

        {/* Live Exchange Balance Summary */}
        {liveBalance && (
          <div className="mb-8">
            <h2 className="text-heading-2 font-semibold text-primary-custom mb-4">Live Exchange Balances</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {liveBalance.aggregated && Object.keys(liveBalance.aggregated).length > 0 ? (
                Object.entries(liveBalance.aggregated).map(([currency, data]: [string, any]) => (
                  <SummaryCard
                    key={currency}
                    title={`${currency} Balance`}
                    value={formatCurrency(data.totalBalance, currency === 'USD' ? 'USD' : currency)}
                    change={0}
                    icon={CurrencyDollarIcon}
                    trend="neutral"
                  />
                ))
              ) : (
                <div className="col-span-full">
                  <div className="card p-6 text-center">
                    <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-heading-3 text-primary-custom mb-2">No Exchange Balances</h3>
                    <p className="text-body text-secondary-custom">
                      Your connected exchanges (Kraken) show zero balances. 
                      Fund your exchange account to start live trading.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Paper Trading Portfolio Summary */}
        <div className="mb-8">
          <h2 className="text-heading-2 font-semibold text-primary-custom mb-4">Paper Trading Portfolio</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              title="Total Portfolio Value"
              value={formatCurrency(portfolio?.totalValue || 0)}
              change={portfolio?.totalReturnPercentage}
              changeType="percentage"
              icon={CurrencyDollarIcon}
              trend={portfolio?.totalReturn && portfolio.totalReturn > 0 ? 'up' : 'down'}
            />
            
            <SummaryCard
              title="Today's P&L"
              value={formatCurrency(portfolio?.dayChange || 0)}
              change={portfolio?.dayChangePercentage}
              changeType="percentage"
              icon={portfolio?.dayChange && portfolio.dayChange > 0 ? ChevronUpIcon : ChevronDownIcon}
              trend={portfolio?.dayChange && portfolio.dayChange > 0 ? 'up' : 'down'}
            />
            
            <SummaryCard
              title="Active Positions"
              value={portfolio?.positions?.length || 0}
              icon={ChartBarIcon}
            />
            
            <SummaryCard
              title="Cash Available"
              value={formatCurrency(portfolio?.cashAvailable || 0)}
              icon={CurrencyDollarIcon}
            />
          </div>
        </div>

        {/* Market Sentiment Row */}
        <div className="mb-6">
          <MarketSentimentWidget 
            fearGreedData={fearGreedData} 
            newsSentimentData={newsSentimentData}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Chart */}
          <div className="card">
            <h3 className="text-heading-3 text-primary-custom mb-4">Portfolio Performance</h3>
            <PerformanceChart height={300} />
          </div>

          {/* Allocation Chart */}
          <div className="card">
            <h3 className="text-heading-3 text-primary-custom mb-4">Portfolio Allocation</h3>
            <AllocationChart height={300} />
          </div>
        </div>

        {/* Performance Metrics */}
        {performance && (
          <div className="card">
            <h3 className="text-heading-3 text-primary-custom mb-6">Performance Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              <div className="text-center">
                <div className="text-data-large text-bullish">
                  {formatPercentage(portfolio?.totalReturnPercentage || 0)}
                </div>
                <div className="text-caption text-secondary-custom mt-1">Total Return</div>
              </div>
              
              <div className="text-center">
                <div className="text-data-large text-primary-custom">
                  {(performance.sharpeRatio || 0).toFixed(2)}
                </div>
                <div className="text-caption text-secondary-custom mt-1">Sharpe Ratio</div>
              </div>
              
              <div className="text-center">
                <div className="text-data-large text-bearish">
                  {formatPercentage(performance.maxDrawdown || 0)}
                </div>
                <div className="text-caption text-secondary-custom mt-1">Max Drawdown</div>
              </div>
              
              <div className="text-center">
                <div className="text-data-large text-bullish">
                  {formatPercentage(performance.winRate || 0)}
                </div>
                <div className="text-caption text-secondary-custom mt-1">Win Rate</div>
              </div>
              
              <div className="text-center">
                <div className="text-data-large text-neutral">
                  {formatPercentage(performance.volatility || 0)}
                </div>
                <div className="text-caption text-secondary-custom mt-1">Volatility</div>
              </div>
              
              <div className="text-center">
                <div className="text-data-large text-primary-custom">
                  {(performance.profitFactor || 0).toFixed(2)}
                </div>
                <div className="text-caption text-secondary-custom mt-1">Profit Factor</div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Trades */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-heading-3 text-primary-custom">Recent Trades</h3>
            <a 
              href="/trades" 
              className="text-body font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              View All →
            </a>
          </div>
          
          <RecentTradesTable />
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h3 className="text-heading-3 text-primary-custom mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href="/strategies"
              className="card p-4 text-center hover:shadow-professional transition-all duration-200 border-primary-custom hover:border-secondary-custom"
            >
              <ChartBarIcon className="h-8 w-8 text-blue-500 mx-auto mb-3" />
              <span className="text-body font-medium text-primary-custom">Manage Strategies</span>
            </a>
            
            <a
              href="/trading-pairs"
              className="card p-4 text-center hover:shadow-professional transition-all duration-200 border-primary-custom hover:border-secondary-custom"
            >
              <CurrencyDollarIcon className="h-8 w-8 text-blue-500 mx-auto mb-3" />
              <span className="text-body font-medium text-primary-custom">Trading Pairs</span>
            </a>
            
            <a
              href="/paper-trading"
              className="card p-4 text-center hover:shadow-professional transition-all duration-200 border-primary-custom hover:border-secondary-custom"
            >
              <svg className="h-8 w-8 text-blue-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-body font-medium text-primary-custom">Paper Trading</span>
            </a>
            
            <a
              href="/analytics"
              className="card p-4 text-center hover:shadow-professional transition-all duration-200 border-primary-custom hover:border-secondary-custom"
            >
              <svg className="h-8 w-8 text-blue-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-body font-medium text-primary-custom">Analytics</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 